import { useCallback, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import StatusBadge from "components/common/StatusBadge";
import { getDocumentTitle } from "utils/helpers";
import { formatCurrency } from "utils/currency";
import { getWorkOrderReport } from "features/WorkOrders/services/WorkOrderService";
import { workOrderStatusMap } from "features/WorkOrders/constants/work-order.constants";
import WorkOrderListFiltersComponent, {
  WorkOrderListFiltersState,
  emptyWorkOrderListFilters,
  workOrderListFiltersToListParams,
} from "features/WorkOrders/WorkOrderList/WorkOrderListFilters";
import type {
  WorkOrderReportRow,
  WorkOrderReportSummary,
} from "features/WorkOrders/types/work-order.types";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const FINANCIALS_MODE_COLOR: Record<string, string> = {
  ACTUAL: "success",
  ESTIMATED: "warning",
  PROJECTED: "secondary",
};

const COLUMNS: ColumnDef<WorkOrderReportRow>[] = [
  {
    header: "Work Order",
    id: "code",
    enableColumnFilter: false,
    enableSorting: false,
    size: 160,
    cell: ({ row }) => {
      const r = row.original;
      return (
        <div>
          <Link to={`/work-orders/${r.id}`} className="fw-medium text-body">
            {r.code ?? `#${r.id}`}
          </Link>
          {r.title && (
            <div
              className="text-muted small text-truncate"
              style={{ maxWidth: 180 }}
            >
              {r.title}
            </div>
          )}
        </div>
      );
    },
  },
  {
    header: "Status",
    accessorKey: "status",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => {
      const v = getValue() as string | undefined;
      if (!v) return <span className="text-muted">—</span>;
      return <StatusBadge value={v} map={workOrderStatusMap} />;
    },
  },
  {
    header: "Agreed Amt",
    accessorKey: "agreedAmount",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ getValue }) => {
      const v = getValue() as number | undefined;
      return v != null ? (
        <span className="tabular-nums">{formatCurrency(v)}</span>
      ) : (
        <span className="text-muted">—</span>
      );
    },
  },
  {
    header: "Revenue",
    id: "revenue",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }) => {
      const f = row.original.financials;
      if (!f) return <span className="text-muted">—</span>;
      return (
        <div className="d-flex align-items-center gap-1">
          <span className="tabular-nums">{formatCurrency(f.revenue)}</span>
          <Badge
            color={FINANCIALS_MODE_COLOR[f.mode] ?? "secondary"}
            pill
            className="fs-10"
          >
            {f.mode[0]}
          </Badge>
        </div>
      );
    },
  },
  {
    header: "Cost",
    id: "totalCost",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }) => {
      const f = row.original.financials;
      return f ? (
        <span className="tabular-nums">{formatCurrency(f.totalCost)}</span>
      ) : (
        <span className="text-muted">—</span>
      );
    },
  },
  {
    header: "Profit",
    id: "profit",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }) => {
      const f = row.original.financials;
      if (!f) return <span className="text-muted">—</span>;
      return (
        <div>
          <span
            className={`tabular-nums fw-semibold ${f.isProfit ? "text-success" : "text-danger"}`}
          >
            {f.isProfit ? "" : "-"}
            {formatCurrency(Math.abs(f.profit))}
          </span>
          <div className="text-muted small tabular-nums">
            {f.margin.toFixed(1)}%
          </div>
        </div>
      );
    },
  },
  {
    header: "Invoiced",
    id: "invoiced",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }) => {
      const b = row.original.billing;
      return b ? (
        <span className="tabular-nums">{formatCurrency(b.totalInvoiced)}</span>
      ) : (
        <span className="text-muted">—</span>
      );
    },
  },
  {
    header: "Outstanding",
    id: "outstanding",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }) => {
      const b = row.original.billing;
      if (!b) return <span className="text-muted">—</span>;
      return (
        <span
          className={`tabular-nums fw-semibold ${b.outstanding > 0 ? "text-danger" : "text-muted"}`}
        >
          {formatCurrency(b.outstanding)}
        </span>
      );
    },
  },
];

function buildCsv(rows: WorkOrderReportRow[]): string {
  const UTF8_BOM = "﻿";
  const esc = (v: string | null | undefined) => {
    const s = v ?? "";
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const num = (v: number | null | undefined) => (v != null ? String(v) : "");

  const header = [
    "Code",
    "Title",
    "Status",
    "Source Type",
    "Actual Start",
    "Actual End",
    "Agreed Amount",
    "Final Billed Amount",
    "Financials Mode",
    "Revenue",
    "Items Cost",
    "Expense Cost",
    "Subcontract Cost",
    "Total Cost",
    "Profit",
    "Margin %",
    "Is Profit",
    "Total Invoiced",
    "Total Received",
    "Outstanding",
  ];
  const lines = [header.join(",")];

  for (const r of rows) {
    const f = r.financials;
    const b = r.billing;
    lines.push(
      [
        esc(r.code),
        esc(r.title),
        esc(r.status),
        esc(r.sourceType),
        esc(r.actualStartDate),
        esc(r.actualEndDate),
        num(r.agreedAmount),
        num(r.finalAmount),
        esc(f?.mode),
        num(f?.revenue),
        num(f?.itemsCost),
        num(f?.expenseCost),
        num(f?.subcontractCost),
        num(f?.totalCost),
        num(f?.profit),
        f?.margin != null ? f.margin.toFixed(2) : "",
        f?.isProfit != null ? (f.isProfit ? "Yes" : "No") : "",
        num(b?.totalInvoiced),
        num(b?.totalReceived),
        num(b?.outstanding),
      ].join(","),
    );
  }

  return UTF8_BOM + lines.join("\n");
}

function triggerCsvDownload(content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `work_orders_report_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const SUMMARY_STATS: {
  key: keyof WorkOrderReportSummary;
  label: string;
  isCurrency?: boolean;
  color?: (v: number) => string;
}[] = [
  { key: "workOrderCount", label: "Work orders" },
  { key: "totalAgreedAmount", label: "Agreed amount", isCurrency: true },
  { key: "totalFinalAmount", label: "Final billed", isCurrency: true },
  { key: "totalInvoiced", label: "Invoiced", isCurrency: true },
  {
    key: "totalReceived",
    label: "Received",
    isCurrency: true,
    color: (v) => (v > 0 ? "text-success" : ""),
  },
  {
    key: "totalOutstanding",
    label: "Outstanding",
    isCurrency: true,
    color: (v) => (v > 0 ? "text-danger" : "text-success"),
  },
];

const WorkOrderReportPage = () => {
  document.title = getDocumentTitle("Work order report");

  const [filters, setFilters] = useState<WorkOrderListFiltersState>(
    emptyWorkOrderListFilters,
  );
  const [summary, setSummary] = useState<WorkOrderReportSummary | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchApi = useCallback(
    async ({ page, limit, search }: { page: number; limit: number; search?: string }) => {
      const res = await getWorkOrderReport({
        ...workOrderListFiltersToListParams(filters),
        page,
        limit,
        search,
      });
      if (res.success && res.data) {
        const raw = res.data as typeof res.data & {
          summary?: WorkOrderReportSummary;
        };
        if (raw.summary) setSummary(raw.summary);
      }
      return res;
    },
    [filters],
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getWorkOrderReport({
        ...workOrderListFiltersToListParams(filters),
        page: 1,
        limit: 9999,
      });
      const rows = (res.data?.rows ?? []) as WorkOrderReportRow[];
      if (!rows.length) {
        toast.info("No data to export");
        return;
      }
      triggerCsvDownload(buildCsv(rows));
      toast.success("Report downloaded");
    } catch {
      toast.error("Could not export report");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Work order report" pageTitle="Reports" />

        {/* Summary cards */}
        {summary && (
          <Row className="g-3 mb-4">
            {SUMMARY_STATS.map(({ key, label, isCurrency, color }) => {
              const val = summary[key] as number;
              return (
                <Col key={key} xs={6} sm={4} md={2}>
                  <div className="p-3 rounded border bg-white text-center">
                    <div className={`fs-5 fw-bold ${color ? color(val) : ""}`}>
                      {isCurrency ? formatCurrency(val, { compact: true }) : val}
                    </div>
                    <div className="text-muted small mt-1">{label}</div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        <Card>
          <CardHeader className="border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h5 className="card-title mb-1">Work order financials</h5>
              <p className="text-muted mb-0 small">
                Revenue, cost, profit, and billing status per work order.
              </p>
            </div>
            <Button
              color="light"
              className="btn-label"
              disabled={exporting}
              onClick={() => void handleExport()}
            >
              {exporting ? (
                <Spinner size="sm" className="label-icon" />
              ) : (
                <i className="ri-download-2-line label-icon align-middle fs-16" />
              )}
              Export CSV
            </Button>
          </CardHeader>

          <CardBody className="pt-3">
            <TableContainer<WorkOrderReportRow>
              columns={COLUMNS}
              fetchApi={fetchApi}
              pageSize={20}
              globalSearch={true}
              SearchPlaceholder="Search work orders..."
              divClass="table-responsive table-card mb-0"
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light text-muted"
              toolbarRight={
                <WorkOrderListFiltersComponent
                  filters={filters}
                  onChange={setFilters}
                />
              }
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default WorkOrderReportPage;
